/**
 * DOMRenderer - Renders timeline elements in the DOM
 * Handles the rendering of headers, task rows, blocks, and other UI elements
 */
class DOMRenderer {
  /**
   * Create a DOMRenderer
   * @param {TimelineCalendar} timeline - The timeline instance
   */
  constructor(timeline) {
    this.timeline = timeline;

    this.cache = {
      lastRenderedBlocks: new Map(),
      elementsCache: new Map()
    };

    this.renderSettings = {
      useOptimization: true,
      animateChanges: true,
      useVirtualScroll: false
    };
  }

  /**
   * แปลง 'YYYY-MM-DD' เป็น Date ของเวลาท้องถิ่น
   * new Date('2026-02-14') ถูกตีความเป็น UTC เที่ยงคืน ซึ่งเลื่อนไปหนึ่งวันในโซนเวลาติดลบ
   * ทำให้เลขวัน ชื่อวัน และการตัดเดือนเพี้ยนทั้งแถว
   * @param {string} isoDate
   * @returns {Date}
   */
  static parseIsoDate(isoDate) {
    const [year, month, day] = String(isoDate).split('-').map(Number);

    return new Date(year, (month || 1) - 1, day || 1);
  }

  /**
   * คลาสสถานะของหนึ่งวัน ใช้ร่วมกันระหว่างหัวตารางกับคอลัมน์ในแถว
   * เพื่อให้แถบสีของทั้งสองฝั่งตรงกันเสมอ
   * @param {string} isoDate
   * @returns {{classes: string[], holiday: Object|null}}
   */
  dayStateOf(isoDate) {
    const classes = [];

    // เสาร์-อาทิตย์เป็นเรื่องของฝั่ง client ล้วนๆ ตาราง holidays เก็บแต่วันหยุดนักขัตฤกษ์
    // (มีคอลัมน์ date อย่างเดียว) จึงไม่เคยส่ง type=weekend มาให้
    const weekEnds = this.timeline.options.weekEnds || [0, 6];
    if (weekEnds.includes(DOMRenderer.parseIsoDate(isoDate).getDay())) {
      classes.push('weekend');
    }

    const holiday = this.timeline.dataManager.holidays.get(isoDate) || null;
    if (holiday) {
      classes.push(holiday.type || 'holiday');
    }

    return {classes, holiday};
  }

  /**
   * Render day headers with current language
   */
  renderDayHeaders() {
    if (!this.timeline.dayHeaders) return;

    // ตรวจสอบว่ามี headers หรือไม่
    if (!this.timeline.headers || this.timeline.headers.length === 0) {
      console.warn('No headers to render');
      return;
    }

    this.timeline.dayHeaders.innerHTML = '';

    // ความกว้างรวมของ timeline คำนวณจากจำนวน headers เสมอ
    // (ห้ามอ่านจาก offsetWidth เพราะได้ความกว้างที่มองเห็นของ scroll container ไม่ใช่ความกว้างจริง)
    const totalWidth = this.timeline.headers.length * this.timeline.options.itemWidth;
    this.timeline.timeLineWidth = totalWidth;
    this.timeline.dayHeaders.style.width = `${totalWidth}px`;

    const monthsContainer = document.createElement('div');
    monthsContainer.className = 'months-container';
    monthsContainer.style.width = `${totalWidth}px`;
    this.timeline.dayHeaders.appendChild(monthsContainer);

    const daysContainer = document.createElement('div');
    daysContainer.className = 'days-container';
    daysContainer.style.width = `${totalWidth}px`;
    this.timeline.dayHeaders.appendChild(daysContainer);

    let currentMonthIndex = -1;
    let currentMonthWidth = 0;
    let currentMonthStart = 0;
    let monthColumns = [];

    // วนลูปสร้าง day headers
    this.timeline.headers.forEach((isoDate, index) => {
      const date = DOMRenderer.parseIsoDate(isoDate);
      const dayNumber = date.getDate();
      const dayIndex = date.getDay();
      const monthIndex = date.getMonth();

      const dayHeader = document.createElement('div');
      dayHeader.className = 'day-header';
      dayHeader.style.width = `${this.timeline.options.itemWidth}px`;
      dayHeader.dataset.date = isoDate;
      dayHeader.dataset.day = index + 1;

      dayHeader.innerHTML = `
      <div class="day-number">${dayNumber}</div>
      <div class="day-name">${this.timeline.dayNames[dayIndex]}</div>
    `;

      // เพิ่ม class weekend/holiday ให้ตรงกับคอลัมน์ในแถวงาน
      const state = this.dayStateOf(isoDate);
      state.classes.forEach(name => dayHeader.classList.add(name));

      // เพิ่ม tooltip แสดงชื่อวันหยุด
      if (state.holiday && state.holiday.name) {
        dayHeader.title = state.holiday.name;
      }

      daysContainer.appendChild(dayHeader);

      if (monthIndex !== currentMonthIndex) {
        if (currentMonthIndex !== -1) {
          monthColumns.push({
            month: currentMonthIndex,
            startIndex: currentMonthStart,
            width: currentMonthWidth
          });
        }

        currentMonthIndex = monthIndex;
        currentMonthStart = index;
        currentMonthWidth = 0;
      }

      currentMonthWidth += this.timeline.options.itemWidth;

      if (index === this.timeline.headers.length - 1) {
        monthColumns.push({
          month: currentMonthIndex,
          startIndex: currentMonthStart,
          width: currentMonthWidth
        });
      }
    });

    monthColumns.forEach(monthData => {
      const monthHeader = document.createElement('div');
      monthHeader.className = 'month-header';
      // เส้นแบ่งของ day-column วาดอยู่ที่ขอบขวา (index * itemWidth - 1)
      // เดือนที่ไม่ใช่เดือนแรกจึงต้องขยับมาทับเส้นนั้น 1px เพื่อให้เส้นแบ่งตรงกัน
      const edgeOffset = monthData.startIndex === 0 ? 0 : 1;
      monthHeader.style.width = `${monthData.width + edgeOffset}px`;
      monthHeader.style.left = `${monthData.startIndex * this.timeline.options.itemWidth - edgeOffset}px`;

      const firstDateOfMonth = DOMRenderer.parseIsoDate(this.timeline.headers[monthData.startIndex]);
      const showYear = monthData.month === 0 ||
        (monthData.startIndex > 0 &&
          firstDateOfMonth.getFullYear() !==
          DOMRenderer.parseIsoDate(this.timeline.headers[0]).getFullYear());

      // ชื่อเดือนถูก stick ไว้ที่ขอบซ้ายของ scrollport (ดู .month-header > span ใน CSS)
      // เมื่อเลื่อน timeline ชื่อเดือนของช่วงที่กำลังแสดงอยู่จึงค้างอยู่ซ้ายสุดเสมอ
      // และถูกดันออกไปโดยเดือนถัดไปเมื่อเลื่อนพ้นเดือนนั้น
      const span = document.createElement('span');
      if (showYear) {
        span.textContent = `${this.timeline.monthNames[monthData.month]} ${firstDateOfMonth.getFullYear()}`;
      } else {
        span.textContent = this.timeline.monthNames[monthData.month];
      }
      monthHeader.appendChild(span);

      monthsContainer.appendChild(monthHeader);
    });
  }

  /**
   * Render the header area with tree structure from tree_nodes
   */
  renderHeaderArea() {
    if (!this.timeline.headerArea || this.timeline.headers.length === 0) return;

    this.timeline.headerArea.innerHTML = '';
    this.timeline.headerArea.style.width = `${this.timeline.options.headerWidth}px`;

    // เพิ่ม header spacer สำหรับช่องว่างด้านบน
    const headerSpacer = document.createElement('div');
    headerSpacer.className = 'header-spacer';
    if (this.timeline.dayHeaders) {
      headerSpacer.style.height = `${this.timeline.dayHeaders.offsetHeight}px`;
    }
    this.timeline.headerArea.appendChild(headerSpacer);

    // ตรวจสอบว่ามีข้อมูลต้นไม้หรือไม่
    if (!this.timeline.headerStructure || !this.timeline.headerStructure.mainGroups) {
      console.warn(this.timeline.t('Tree data not loaded'));
      return;
    }

    let rowIndex = 0;

    // วนลูปสร้าง header สำหรับแต่ละกลุ่มหลัก (level 1)
    for (const mainGroup of this.timeline.headerStructure.mainGroups) {
      // สร้างหัวข้อกลุ่มหลัก (เช่น "Room")
      const groupHeader = document.createElement('div');
      groupHeader.className = 'task-group-header';
      groupHeader.textContent = mainGroup.name;
      groupHeader.dataset.id = mainGroup.id;
      groupHeader.style.width = `${this.timeline.options.headerWidth}px`;
      this.timeline.headerArea.appendChild(groupHeader);

      if (!this.timeline.groupHeaderHeight) {
        this.timeline.groupHeaderHeight = groupHeader.offsetHeight || 30;
      }

      // สร้าง container สำหรับกลุ่มย่อย
      const groupContainer = document.createElement('div');
      groupContainer.className = 'group-container';

      // ดึงกลุ่มย่อยของกลุ่มหลักนี้ (level 2)
      const subGroups = this.timeline.headerStructure.subGroups[mainGroup.id] || [];

      // วนลูปสร้าง UI สำหรับแต่ละกลุ่มย่อย
      for (const subGroup of subGroups) {
        // สร้าง container สำหรับกลุ่มย่อยและงานของมัน
        const subgroupContainer = document.createElement('div');
        subgroupContainer.className = 'subgroup-container';

        // สร้างหัวข้อกลุ่มย่อย (เช่น "Floor 1")
        const subgroupHeader = document.createElement('div');
        subgroupHeader.className = 'subgroup';
        const subgroup = document.createElement('span');
        subgroup.textContent = subGroup.name;
        subgroupHeader.appendChild(subgroup);
        subgroupContainer.appendChild(subgroupHeader);

        const tasks = subGroup.tasks || [];

        // สร้าง container สำหรับงานทั้งหมดในกลุ่มย่อย
        const tasksContainer = document.createElement('div');
        tasksContainer.className = 'tasks-container';

        // คำนวณความสูงของ subgroup เท่ากับจำนวน task * ความสูงของ task
        const taskHeight = 40; // ปรับตามความสูงจริงของ task-header
        const subgroupHeight = Math.max(tasks.length * taskHeight, taskHeight); // อย่างน้อย 1 task height

        // ถ้าไม่มีงาน สร้าง task-header เปล่า 1 อัน
        if (tasks.length === 0) {
          const emptyTask = document.createElement('div');
          emptyTask.className = 'task-header empty';
          emptyTask.style.height = `${taskHeight}px`;
          emptyTask.dataset.rowIndex = rowIndex;
          tasksContainer.appendChild(emptyTask);
          rowIndex++;
        } else {
          // สร้าง task-header สำหรับแต่ละงานในกลุ่มย่อย
          for (const task of tasks) {
            const taskHeader = document.createElement('div');
            taskHeader.className = 'task-header';
            const span = document.createElement('span');
            span.textContent = task.name;
            span.title = task.name;
            taskHeader.appendChild(span);
            taskHeader.dataset.taskId = task.id;
            taskHeader.dataset.rowIndex = rowIndex;
            taskHeader.style.height = `${taskHeight}px`;
            tasksContainer.appendChild(taskHeader);
            rowIndex++;
          }
        }

        subgroupContainer.appendChild(tasksContainer);
        groupContainer.appendChild(subgroupContainer);
      }

      this.timeline.headerArea.appendChild(groupContainer);
    }

    // เก็บจำนวนแถวทั้งหมดไว้ใช้ในการสร้าง task rows
    this.timeline.totalRows = rowIndex;
  }

  /**
   * Render task rows for the timeline
   */
  renderTaskRows() {
    if (!this.timeline.tasksArea) return;

    this.timeline.tasksArea.innerHTML = '';
    // ล้างรายการเดิมทิ้ง มิฉะนั้นจะสะสม element ที่ถูกถอดออกจาก DOM ไปแล้วทุกครั้งที่ render
    this.timeline.taskGroupHeaders = [];

    // ใช้จำนวนแถวที่คำนวณได้จาก headerStructure
    const totalRows = this.timeline.totalRows || 0;

    if (totalRows === 0) return;

    // วนลูปผ่านแต่ละกลุ่มหลักเพื่อสร้างแถวงาน
    for (const mainGroup of this.timeline.headerStructure.mainGroups) {
      // สร้าง task-group-header อ้างอิงถึงกลุ่มหลัก (แทน main-group-header)
      const groupHeader = document.createElement('div');
      groupHeader.className = 'task-group-header';
      groupHeader.dataset.groupId = mainGroup.id;
      this.timeline.tasksArea.appendChild(groupHeader);

      // เก็บ groupHeader ไว้สำหรับปรับขนาดในภายหลัง
      this.timeline.taskGroupHeaders.push(groupHeader);

      const subGroups = this.timeline.headerStructure.subGroups[mainGroup.id] || [];

      // สร้างแถวงานภายใต้กลุ่มหลักนี้
      for (const subGroup of subGroups) {
        const tasks = subGroup.tasks || [];

        // ถ้าไม่มีงาน ให้สร้างแถวว่างหนึ่งแถว
        if (tasks.length === 0) {
          this.createEmptyTaskRow(subGroup.startRowIndex);
        } else {
          // สร้างแถวสำหรับแต่ละงาน
          for (let i = 0; i < tasks.length; i++) {
            const task = tasks[i];
            this.createTaskRow(task.rowIndex, mainGroup.id, subGroup.id);
          }
        }
      }
    }

    // ความกว้างรวมต้องคำนวณจากจำนวนวัน x itemWidth ให้ตรงกับ day-headers
    // ไม่ใช่ offsetWidth ของ .task-timeline ซึ่งเท่ากับความกว้างที่มองเห็นของ scroll container
    this.timeline.timeLineWidth = this.timeline.headers.length * this.timeline.options.itemWidth;
    this.timeline.tasksArea.style.width = `${this.timeline.timeLineWidth}px`;

    // กำหนดความกว้างให้กับทุกแถว
    this.timeline.tasksArea.querySelectorAll('.task-row').forEach(row => {
      row.style.width = `${this.timeline.timeLineWidth}px`;
    });

    // กำหนดความสูงและความกว้างของ task-group-header
    if (this.timeline.taskGroupHeaders && this.timeline.taskGroupHeaders.length > 0) {
      let maxHeight = this.timeline.groupHeaderHeight || 30; // ค่าเริ่มต้น 30px หากไม่ได้กำหนด

      this.timeline.taskGroupHeaders.forEach(header => {
        header.style.height = `${maxHeight}px`;
        header.style.lineHeight = `${maxHeight}px`;
        header.style.width = `${this.timeline.timeLineWidth}px`;
      });
    }

    // กำหนดความกว้างของ day headers ให้ตรงกับพื้นที่งาน
    if (this.timeline.dayHeaders) {
      this.timeline.dayHeaders.style.width = `${this.timeline.timeLineWidth}px`;
    }
  }

  /**
   * สร้างคอลัมน์วันที่ของหนึ่งแถว
   * ต้องสร้างครบทุก header เสมอ เพื่อให้เส้นแบ่งตรงกับ .day-header ด้านบน
   * (วันหยุดถูกคัดออกตั้งแต่ตอนสร้าง headers แล้วเมื่อ showHolidays = false)
   * @param {HTMLElement} taskTimeline - element ที่จะใส่คอลัมน์
   * @param {number} rowIndex - ลำดับแถว
   * @private
   */
  _renderDayColumns(taskTimeline, rowIndex) {
    this.timeline.headers.forEach((isoDate, index) => {
      const dayColumn = document.createElement('div');
      dayColumn.className = 'day-column';
      dayColumn.style.width = `${this.timeline.options.itemWidth}px`;
      dayColumn.dataset.date = isoDate;
      dayColumn.dataset.day = index + 1;
      dayColumn.dataset.rowIndex = rowIndex;

      // ใช้ตัวเดียวกับหัวตาราง แถบสีของทั้งสองฝั่งจึงตรงกันเสมอ
      this.dayStateOf(isoDate).classes.forEach(name => dayColumn.classList.add(name));

      // เพิ่มคลาส 'disabled-click' ตามสถานะโหมดคลิกเพิ่มบล็อก
      if (!this.timeline.isClickToAddMode) {
        dayColumn.classList.add('disabled-click');
      }

      // เพิ่ม event listeners
      dayColumn.addEventListener('click', (e) => {
        if (this.timeline.markerManager.isMarkerMode) {
          // marker.day เป็นดัชนีฐาน 0 ของ header (ดู WorkingDaysManager.markerDayToDate)
          // ต่างจาก handleDayClick ที่รับเลขวันฐาน 1 แล้วลบ 1 เอง
          this.timeline.markerManager.addMarker(index, rowIndex);
        } else if (!this.timeline.connectionManager.isConnectionMode) {
          // ตรวจสอบสถานะโหมดคลิกเพิ่มบล็อกก่อนเรียกใช้ handleDayClick
          if (this.timeline.isClickToAddMode) {
            this.timeline.eventHandler.handleDayClick(e, index + 1, rowIndex);
          }
        }
      });

      taskTimeline.appendChild(dayColumn);
    });
  }

  // เพิ่มเมธอดช่วยสำหรับสร้างแถวงาน
  createTaskRow(rowIndex, mainGroupId, subGroupId) {
    const taskRow = document.createElement('div');
    taskRow.className = 'task-row';
    taskRow.dataset.rowIndex = rowIndex;
    taskRow.dataset.mainGroupId = mainGroupId;
    taskRow.dataset.subGroupId = subGroupId;

    const taskTimeline = document.createElement('div');
    taskTimeline.className = 'task-timeline';
    this._renderDayColumns(taskTimeline, rowIndex);

    taskRow.appendChild(taskTimeline);
    this.timeline.tasksArea.appendChild(taskRow);

    return taskRow;
  }

  // เพิ่มเมธอดช่วยสำหรับสร้างแถวว่าง
  createEmptyTaskRow(rowIndex) {
    const taskRow = document.createElement('div');
    taskRow.className = 'task-row';
    taskRow.dataset.rowIndex = rowIndex;

    const taskTimeline = document.createElement('div');
    taskTimeline.className = 'task-timeline';
    this._renderDayColumns(taskTimeline, rowIndex);

    taskRow.appendChild(taskTimeline);
    this.timeline.tasksArea.appendChild(taskRow);

    return taskRow;
  }

  /**
   * Render all blocks in the timeline
   */
  renderBlocks() {
    const currentBlockIds = new Set(this.timeline.dataManager.blocks.map(block => block.id || block.tempId));

    document.querySelectorAll('.task-block').forEach(el => {
      const blockId = el.dataset.id;
      if (!currentBlockIds.has(blockId)) {
        el.remove();
        this.cache.lastRenderedBlocks.delete(blockId);
      }
    });

    requestAnimationFrame(() => {
      this.timeline.dataManager.blocks.forEach(block => {
        this.timeline.dataManager.updateBlockDates(block);
        const blockElement = document.querySelector(`.task-block[data-id="${block.id || block.tempId}"]`);
        this.renderBlock(block, blockElement);
      });
      this.timeline.connectionManager.updateConnections();
    });
  }

  /**
   * Render a single block
   * @param {Object} block - Block data
   * @param {HTMLElement} blockElement - Block DOM element or null to create new
   */
  renderBlock(block, blockElement) {
    if (!block.isVisible) {
      if (blockElement) {
        blockElement.remove();
      }
      return;
    }

    const taskRow = document.querySelector(`.task-row[data-row-index="${block.rowIndex}"]`);
    if (!taskRow) return;

    const taskTimeline = taskRow.querySelector('.task-timeline');
    if (!taskTimeline) return;

    let hasChanged = true;
    const blockId = block.id || block.tempId;

    if (this.cache.lastRenderedBlocks.has(blockId)) {
      const lastBlock = this.cache.lastRenderedBlocks.get(blockId);

      hasChanged = (
        lastBlock.left !== block.left ||
        lastBlock.width !== block.width ||
        lastBlock.rowIndex !== block.rowIndex ||
        lastBlock.displayText !== block.displayText ||
        lastBlock.color !== block.color ||
        lastBlock.icon !== block.icon ||
        lastBlock.progress !== block.progress
      );
    }

    if (!hasChanged && blockElement) {
      return;
    }

    this.cache.lastRenderedBlocks.set(blockId, {...block});

    if (blockElement === null) {
      blockElement = document.createElement('div');
      blockElement.className = 'task-block';
      blockElement.dataset.id = blockId;

      blockElement.dataset.startDate = block.startDate;
      blockElement.dataset.endDate = block.endDate;
      blockElement.dataset.duration = block.duration;
      blockElement.dataset.rowIndex = block.rowIndex;
      blockElement.dataset.color = block.color;
      blockElement.dataset.displayText = block.displayText;
      blockElement.dataset.progress = block.progress;

      if (block.progress !== undefined) {
        const progressBar = document.createElement('div');
        progressBar.className = 'task-progress';
        progressBar.style.width = `${block.progress}%`;
        blockElement.appendChild(progressBar);
      }

      if (block.displayText) {
        const textElement = document.createElement('span');
        textElement.textContent = this.timeline.escapeHtml(block.displayText);
        blockElement.appendChild(textElement);
        if (block.icon) {
          textElement.className = 'task-text ' + block.icon;
        } else {
          textElement.className = 'task-text';
        }
      }

      const leftHandle = document.createElement('div');
      leftHandle.className = 'resize-handle left';
      leftHandle.dataset.direction = 'left';
      leftHandle.addEventListener('mousedown', (e) => this.timeline.eventHandler.handleResizeHandleMouseDown(e, block, 'left'));
      blockElement.appendChild(leftHandle);
      blockElement.leftHandle = leftHandle;

      const rightHandle = document.createElement('div');
      rightHandle.className = 'resize-handle right';
      rightHandle.dataset.direction = 'right';
      rightHandle.addEventListener('mousedown', (e) => this.timeline.eventHandler.handleResizeHandleMouseDown(e, block, 'right'));
      blockElement.appendChild(rightHandle);
      blockElement.rightHandle = rightHandle;

      blockElement.addEventListener('mousedown', (e) => {
        if (this.timeline.connectionManager.isConnectionMode) {
          this.timeline.connectionManager.handleConnectionClick(block, blockElement);
        } else if (!e.target.classList.contains('resize-handle')) {
          this.timeline.eventHandler.handleBlockMouseDown(e, block);
        }
      });

      // Add click event to handle block selection
      blockElement.addEventListener('click', (e) => {
        // Prevent triggering the click during resize or drag operations
        if (!e.target.classList.contains('resize-handle') &&
          !this.timeline.eventHandler.isDragging &&
          !this.timeline.connectionManager.isConnectionMode) {
          if (typeof this.timeline.connectionManager.selectBlock === 'function') {
            this.timeline.connectionManager.selectBlock(block, blockElement);
          } else {
            console.warn('ConnectionManager.selectBlock is not a function');
            // Fallback: Just add/remove selected class manually
            if (blockElement.classList.contains('selected')) {
              blockElement.classList.remove('selected');
            } else {
              document.querySelectorAll('.task-block.selected').forEach(el => {
                el.classList.remove('selected');
              });
              blockElement.classList.add('selected');
            }
          }
        }
      });

      blockElement.addEventListener('dblclick', () => this.timeline.editBlock(block));
      blockElement.addEventListener('mouseover', (e) => this.showBlockDetailsTooltip(e, block));
      blockElement.addEventListener('mouseout', this.hideTooltip);
      taskTimeline.appendChild(blockElement);

      if (this.renderSettings.animateChanges) {
        blockElement.classList.add('block-new');
        setTimeout(() => {
          blockElement.classList.remove('block-new');
        }, 500);
      }
    } else {
      if (block.progress !== undefined) {
        let progressBar = blockElement.querySelector('.task-progress');
        if (!progressBar) {
          progressBar = document.createElement('div');
          progressBar.className = 'task-progress';
          blockElement.prepend(progressBar);
        }
        progressBar.style.width = `${block.progress}%`;
      }

      let textElement = blockElement.querySelector('.task-text');
      if (block.displayText) {
        if (!textElement) {
          textElement = document.createElement('span');
          blockElement.appendChild(textElement);
        }
        if (block.icon) {
          textElement.className = 'task-text ' + block.icon;
        } else {
          textElement.className = 'task-text';
        }
        textElement.textContent = this.timeline.escapeHtml(block.displayText);
      } else if (textElement) {
        textElement.remove();
      }

      blockElement.dataset.startDate = block.startDate;
      blockElement.dataset.endDate = block.endDate;
      blockElement.dataset.duration = block.duration;
      blockElement.dataset.rowIndex = block.rowIndex;
      blockElement.dataset.color = block.color;
      blockElement.dataset.displayText = block.displayText;
      blockElement.dataset.progress = block.progress;
    }

    blockElement.dataset.rowIndex = block.rowIndex;
    blockElement.style.backgroundColor = block.color;

    blockElement.style.left = `${block.visibleLeft}px`;
    blockElement.style.width = `${block.visibleWidth}px`;

    if (block.isPartiallyVisible) {
      blockElement.classList.add('block-partial-visible');

      if (block.left < 0) {
        blockElement.classList.add('cut-left');
      }
      if (block.left + block.width > block.visibleLeft + block.visibleWidth) {
        blockElement.classList.add('cut-right');
      }
    } else {
      blockElement.classList.remove('block-partial-visible', 'cut-left', 'cut-right');
    }

    if (block.left < 0) {
      blockElement.leftHandle.style.display = 'none';
    } else {
      blockElement.leftHandle.style.display = '';
    }

    if (block.left + block.width > this.timeline.headers.length * this.timeline.options.itemWidth) {
      blockElement.rightHandle.style.display = 'none';
    } else {
      blockElement.rightHandle.style.display = '';
    }
  }

  /**
   * Show details in tooltip
   * @param {Event} e - Mouse event
   * @param {Object} block - Block data
   */
  showBlockDetailsTooltip(e, block) {
    // ใช้ getTaskInfoByRowIndex เพื่อดึงข้อมูลงาน
    const taskInfo = this.timeline.dataManager.getTaskInfoByRowIndex(block.rowIndex);

    const tooltipText = `
      <strong>${this.timeline.t('Task')}:</strong> ${this.timeline.escapeHtml(taskInfo ? taskInfo.name : '')}<br>
      <strong>${this.timeline.t('Group')}:</strong> ${this.timeline.escapeHtml(taskInfo ? taskInfo.groupName : '')}<br>
      <strong>${this.timeline.t('Subgroup')}:</strong> ${this.timeline.escapeHtml(taskInfo ? taskInfo.subGroupName : '')}<br>
      <strong>${this.timeline.t('Start date')}:</strong> ${block.startDate}<br>
      <strong>${this.timeline.t('End date')}:</strong> ${block.endDate}<br>
      <strong>${this.timeline.t('Duration')}:</strong> ${block.duration} ${this.timeline.t('days')}<br>
      <strong>${this.timeline.t('Progress')}:</strong> ${block.progress}%
    `;

    this.showTooltip(e, tooltipText);
  }

  /**
   * Show tooltip with specified content
   * @param {Event} e - Mouse event
   * @param {string} content - HTML content for tooltip
   */
  showTooltip(e, content) {
    this.hideTooltip();

    const tooltip = document.createElement('div');
    tooltip.id = 'block-details-tooltip';
    tooltip.className = 'details-tooltip';
    tooltip.style.position = 'fixed';
    tooltip.style.background = '#fff';
    tooltip.style.border = '1px solid #ccc';
    tooltip.style.borderRadius = '4px';
    tooltip.style.padding = '8px 12px';
    tooltip.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
    tooltip.style.zIndex = '1000';
    tooltip.style.maxWidth = '300px';
    tooltip.style.fontSize = '14px';
    tooltip.style.lineHeight = '1.4';
    tooltip.innerHTML = content;

    document.body.appendChild(tooltip);

    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;

    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = clientX + 10;
    let top = clientY + 10;

    if (left + tooltipWidth > viewportWidth) {
      left = clientX - tooltipWidth - 10;
    }

    if (top + tooltipHeight > viewportHeight) {
      top = clientY - tooltipHeight - 10;
    }

    if (left < 0) left = 10;
    if (top < 0) top = 10;

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  /**
   * Hide block details tooltip
   */
  hideTooltip() {
    const tooltip = document.getElementById('block-details-tooltip');
    if (tooltip) {
      tooltip.remove();
    }
  }

  /**
   * Calculate tooltip position based on mouse event and scroll positions
   * @param {MouseEvent|TouchEvent} event - Mouse or touch event
   * @param {HTMLElement} tooltip - The tooltip element
   * @returns {{left: number, top: number}} - Tooltip position (left, top)
   */
  getTooltipPosition(event, tooltip) {
    const clientX = event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
    const clientY = event.type.startsWith('touch') ? event.touches[0].clientY : event.clientY;

    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = clientX + scrollX + 10;
    let top = clientY + scrollY + 10;

    if (clientX + tooltipWidth + 10 > viewportWidth) {
      left = clientX + scrollX - tooltipWidth - 10;
    }

    if (clientY + tooltipHeight + 10 > viewportHeight) {
      top = clientY + scrollY - tooltipHeight - 10;
    }

    if (left < scrollX) left = scrollX + 10;
    if (top < scrollY) top = scrollY + 10;

    return {left, top};
  }

  /**
   * Calculate the top position for a block based on its row index
   * @param {number} rowIndex - The row index of the block
   * @returns {number} - The top position in pixels
   */
  calculateBlockTop(rowIndex) {
    // Standard row height (adjust based on your timeline's row height)
    const rowHeight = 60; // Assuming each row is 60px high
    const headerOffset = 40; // Offset for headers if any

    return headerOffset + (rowIndex * rowHeight);
  }

  /**
   * Update block position in the DOM
   * @param {HTMLElement} blockElement - The block DOM element
   * @param {Object} block - Block data with position information
   */
  updateBlockPosition(blockElement, block) {
    if (!blockElement || !block) return;

    // Update horizontal position
    if (block.left !== undefined) {
      blockElement.style.left = block.left + 'px';
    }

    // Update vertical position based on row index
    if (block.rowIndex !== undefined) {
      const topPosition = this.calculateBlockTop(block.rowIndex);
      blockElement.style.top = topPosition + 'px';
    }

    // Update width if duration changed
    if (block.duration !== undefined && this.timeline.options.itemWidth) {
      const width = block.duration * this.timeline.options.itemWidth;
      blockElement.style.width = width + 'px';
    }
  }
}

window.DOMRenderer = DOMRenderer;
